import React from 'react';
import { Redirect, Route, RouteProps } from 'react-router';
import { useDispatch } from 'react-redux';
import {
	RejectManualCure,
	RejectNoCure,
	UnknownError,
} from 'stash/components/sign-up/bank/BankErrorScreen';
import {
	manualCurePrimaryCtaClick,
	manualCureScreenView,
	noCureContactClick,
	noCurePrimaryCtaClick,
	noCureScreenView,
	unknownContactClick,
	unknownPrimaryCtaClick,
	unknownScreenView,
} from 'stash/actions/debitSignUpTrackingEvents';
import { AccountStatus, NextStep } from 'stash/components/bank/BankConstants';
import { DisabledFeaturesList } from 'stash/routes/BankRoute';
import EligibilityRoute from 'stash/routes/EligibilityRoute';
import BANK_ACCOUNT_PARTNER_TYPE from 'stash/constants/partnerType';
import type { ReduxDebitAccount } from 'stash/types/redux/entities/bank';

type NextStepRouteProps = RouteProps & {
	feature?: string;
	redirect?: string;
	account: ReduxDebitAccount[];
};

export const NextStepRoute = (props: NextStepRouteProps) => {
	const { children, feature, redirect, account } = props;

	const dispatch = useDispatch();

	const status = account[0].status;
	const nextStep = account[0].nextStep;
	const disabledFeatures = account[0].disabledFeatures;
	const isGreenDotPartner = account[0].partnerType === BANK_ACCOUNT_PARTNER_TYPE.GREENDOT;

	switch (status) {
		case AccountStatus.Normal:
			return handleNormal();
		case AccountStatus.RegistrationNotComplete:
			return handleRegistrationNotComplete();
		case AccountStatus.Restricted:
			return handleRestricted();
		case AccountStatus.Closed:
			return handleClosed();
		case AccountStatus.Pending:
			return handlePending();
		case AccountStatus.Inactive:
			return handleInactive();
		case AccountStatus.Closing:
			return handleClosing();
		case AccountStatus.Locked:
			return handleLocked();
		case AccountStatus.Unknown:
		default:
			return handleUnknown();
	}

	function doDisallowedFeaturesCheckAndContinue() {
		return (
			<Route {...props}>
				{feature && redirect && disabledFeatures?.includes(feature) ? (
					<Redirect to={redirect} />
				) : (
					children
				)}
			</Route>
		);
	}

	function unknownError() {
		return (
			<UnknownError
				handleMount={() => dispatch(unknownScreenView())}
				handleContactClick={() => dispatch(unknownContactClick())}
				handleCtaClick={() => dispatch(unknownPrimaryCtaClick())}
			/>
		);
	}

	function handleNormal() {
		if (nextStep === NextStep.None) {
			return doDisallowedFeaturesCheckAndContinue();
		}
	}

	function handleRegistrationNotComplete() {
		if (nextStep === NextStep.ContactSupport) {
			return (
				<RejectManualCure
					handleMount={() => dispatch(manualCureScreenView())}
					handleCtaClick={() => dispatch(manualCurePrimaryCtaClick())}
				/>
			);
		}

		if (nextStep === NextStep.None) {
			return (
				<RejectNoCure
					handleMount={() => dispatch(noCureScreenView())}
					handleContactClick={() => dispatch(noCureContactClick())}
					handleCtaClick={() => dispatch(noCurePrimaryCtaClick())}
				/>
			);
		}
	}

	function handleRestricted() {
		if (nextStep === NextStep.ContactSupport) {
			return doDisallowedFeaturesCheckAndContinue();
		}

		if (nextStep === NextStep.SpendDown) {
			return doDisallowedFeaturesCheckAndContinue();
		}

		return unknownError();
	}

	function handleClosedGreendotAccount() {
		if (feature === DisabledFeaturesList.Statements) {
			return doDisallowedFeaturesCheckAndContinue();
		}
		return <EligibilityRoute />;
	}

	function handleClosed() {
		if (nextStep === NextStep.ClaimFunds) {
			return doDisallowedFeaturesCheckAndContinue();
		}

		if (isGreenDotPartner) {
			return handleClosedGreendotAccount();
		}

		if (nextStep === NextStep.None) {
			return doDisallowedFeaturesCheckAndContinue();
		}
		return unknownError();
	}

	function handleClosing() {
		if (nextStep === NextStep.None) {
			return doDisallowedFeaturesCheckAndContinue();
		}
		return unknownError();
	}

	function handlePending() {
		if (nextStep === NextStep.None) {
			return doDisallowedFeaturesCheckAndContinue();
		}
		if (nextStep === NextStep.ContactSupport) {
			return doDisallowedFeaturesCheckAndContinue();
		}
		return unknownError();
	}

	function handleInactive() {
		if (nextStep === NextStep.TransferIn) {
			return doDisallowedFeaturesCheckAndContinue();
		}
		if (nextStep === NextStep.None) {
			return doDisallowedFeaturesCheckAndContinue();
		}
		return unknownError();
	}

	function handleLocked() {
		if (nextStep === NextStep.ContactSupport) {
			return doDisallowedFeaturesCheckAndContinue();
		}
		if (nextStep === NextStep.None) {
			return doDisallowedFeaturesCheckAndContinue();
		}

		return unknownError();
	}

	function handleUnknown() {
		return (
			<RejectManualCure
				handleMount={() => dispatch(manualCureScreenView())}
				handleCtaClick={() => dispatch(manualCurePrimaryCtaClick())}
			/>
		);
	}
};

export default NextStepRoute;
